import React, { useState } from 'react';
import { X, Wifi, Eye, EyeOff, Network, RotateCcw, AlertTriangle } from 'lucide-react';
import { prepareCommonHeaders } from '../../services/baseApi';

interface WifiConfigDialogProps {
  isOpen: boolean;
  onClose: () => void;
  baseUnit: {
    id: number;
    serialNo: number;
    base_unit_name: string;
  } | null;
}

type IpMode = 'dhcp' | 'static';
type ActiveTab = 'wifi' | 'ethernet';

interface StaticIpConfig {
  ip_address: string;
  subnet_mask: string;
  gateway: string;
  dns1: string;
  dns2: string;
}

interface WifiConfig {
  ssid: string;
  password: string;
  ip_mode: IpMode;
  static: StaticIpConfig;
}

interface EthernetConfig {
  ip_mode: IpMode;
  static: StaticIpConfig;
}

const emptyStatic: StaticIpConfig = {
  ip_address: '',
  subnet_mask: '',
  gateway: '',
  dns1: '',
  dns2: '',
};

const initialWifi: WifiConfig = {
  ssid: '',
  password: '',
  ip_mode: 'dhcp',
  static: { ...emptyStatic },
};

const initialEthernet: EthernetConfig = {
  ip_mode: 'dhcp',
  static: { ...emptyStatic },
};

function StaticIpFields({
  config,
  onChange,
  disabled,
}: {
  config: StaticIpConfig;
  onChange: (updated: StaticIpConfig) => void;
  disabled: boolean;
}) {
  const field = (label: string, key: keyof StaticIpConfig, placeholder: string, required = true) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input
        type="text"
        required={required}
        value={config[key]}
        onChange={(e) => onChange({ ...config, [key]: e.target.value })}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
          dark:bg-gray-700 dark:text-white text-sm disabled:opacity-50"
      />
    </div>
  );

  return (
    <div className="space-y-3 mt-3 pl-3 border-l-2 border-indigo-200 dark:border-indigo-700">
      {field('IP Address', 'ip_address', '192.168.1.100')}
      {field('Subnet Mask', 'subnet_mask', '255.255.255.0')}
      {field('Gateway', 'gateway', '192.168.1.1')}
      {field('Primary DNS', 'dns1', '8.8.8.8')}
      {field('Secondary DNS', 'dns2', '8.8.4.4', false)}
    </div>
  );
}

function IpModeToggle({
  value,
  onChange,
  disabled,
}: {
  value: IpMode;
  onChange: (mode: IpMode) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden w-fit">
      {(['dhcp', 'static'] as IpMode[]).map((mode) => (
        <button
          key={mode}
          type="button"
          disabled={disabled}
          onClick={() => onChange(mode)}
          className={`px-4 py-1.5 text-sm font-medium capitalize transition-colors disabled:opacity-50
            ${value === mode
              ? 'bg-indigo-600 text-white'
              : 'bg-app-inset dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
            }`}
        >
          {mode.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export default function WifiConfigDialog({ isOpen, onClose, baseUnit }: WifiConfigDialogProps) {
  const [activeTab, setActiveTab] = useState<ActiveTab>('wifi');
  const [wifi, setWifi] = useState<WifiConfig>(initialWifi);
  const [ethernet, setEthernet] = useState<EthernetConfig>(initialEthernet);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!baseUnit) return;

    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const headers = prepareCommonHeaders();
      const requestBody = {
        baseUnitSerialNumber: baseUnit.serialNo,
        baseUnitName: baseUnit.base_unit_name,
        timestamp: new Date().toISOString(),
        wifi: {
          ssid: wifi.ssid,
          password: wifi.password,
          ip_mode: wifi.ip_mode,
          ...(wifi.ip_mode === 'static' ? wifi.static : {}),
        },
        ethernet: {
          ip_mode: ethernet.ip_mode,
          ...(ethernet.ip_mode === 'static' ? ethernet.static : {}),
        },
      };

      const response = await fetch('https://s3.ts1cloud.com/service3/update-wifi', {
        method: 'POST',
        credentials: 'include',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(response.status === 403 ? 'Permission denied' : `HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      setSuccess(true);
      setWifi(initialWifi);
      setEthernet(initialEthernet);

      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update network configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading && !isResetting) {
      setWifi(initialWifi);
      setEthernet(initialEthernet);
      setError(null);
      setSuccess(false);
      setConfirmReset(false);
      setResetSuccess(false);
      setActiveTab('wifi');
      onClose();
    }
  };

  const handleReset = async () => {
    if (!baseUnit) return;

    setIsResetting(true);
    setError(null);

    try {
      const headers = prepareCommonHeaders();
      const requestBody = {
        baseUnitSerialNumber: baseUnit.serialNo,
        baseUnitName: baseUnit.base_unit_name,
        timestamp: new Date().toISOString(),
        action: 'factory_reset_network',
      };

      const response = await fetch('https://s3.ts1cloud.com/service3/update-wifi', {
        method: 'POST',
        credentials: 'include',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(response.status === 403 ? 'Permission denied' : `HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      setConfirmReset(false);
      setResetSuccess(true);

      setTimeout(() => {
        onClose();
        setResetSuccess(false);
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset command');
      setConfirmReset(false);
    } finally {
      setIsResetting(false);
    }
  };

  if (!isOpen || !baseUnit) return null;

  const tabs: { id: ActiveTab; label: string; icon: React.ReactNode }[] = [
    { id: 'wifi', label: 'WiFi', icon: <Wifi className="h-4 w-4 mr-1.5" /> },
    { id: 'ethernet', label: 'Ethernet', icon: <Network className="h-4 w-4 mr-1.5" /> },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-app-panel dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Network Configuration
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {baseUnit.base_unit_name} &middot; {baseUnit.serialNo}
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400 disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center px-6 py-3 text-sm font-medium border-b-2 transition-colors
                ${activeTab === tab.id
                  ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-6 overflow-y-auto flex-1 space-y-4">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-md">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}
            {success && (
              <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-md">
                <p className="text-sm text-green-600 dark:text-green-400">
                  Network configuration updated successfully!
                </p>
              </div>
            )}

            {/* WiFi Tab */}
            {activeTab === 'wifi' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Network Name (SSID) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required={activeTab === 'wifi'}
                    value={wifi.ssid}
                    onChange={(e) => setWifi({ ...wifi, ssid: e.target.value })}
                    placeholder="Enter WiFi network name"
                    disabled={isLoading}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm
                      focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                      dark:bg-gray-700 dark:text-white text-sm disabled:opacity-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required={activeTab === 'wifi'}
                      value={wifi.password}
                      onChange={(e) => setWifi({ ...wifi, password: e.target.value })}
                      placeholder="Enter WiFi password"
                      disabled={isLoading}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm
                        focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                        dark:bg-gray-700 dark:text-white text-sm disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    IP Assignment
                  </label>
                  <IpModeToggle
                    value={wifi.ip_mode}
                    onChange={(mode) => setWifi({ ...wifi, ip_mode: mode })}
                    disabled={isLoading}
                  />
                  {wifi.ip_mode === 'static' && (
                    <StaticIpFields
                      config={wifi.static}
                      onChange={(s) => setWifi({ ...wifi, static: s })}
                      disabled={isLoading}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Ethernet Tab */}
            {activeTab === 'ethernet' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    IP Assignment
                  </label>
                  <IpModeToggle
                    value={ethernet.ip_mode}
                    onChange={(mode) => setEthernet({ ...ethernet, ip_mode: mode })}
                    disabled={isLoading}
                  />
                  {ethernet.ip_mode === 'static' && (
                    <StaticIpFields
                      config={ethernet.static}
                      onChange={(s) => setEthernet({ ...ethernet, static: s })}
                      disabled={isLoading}
                    />
                  )}
                  {ethernet.ip_mode === 'dhcp' && (
                    <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                      The base unit will obtain an IP address automatically via DHCP.
                    </p>
                  )}
                </div>
              </div>
            )}
            {/* Danger Zone */}
            <div className="border border-red-200 dark:border-red-800 rounded-lg p-4 mt-2">
              <div className="flex items-center mb-2">
                <AlertTriangle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0" />
                <span className="text-sm font-medium text-red-600 dark:text-red-400">Danger Zone</span>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                Reset all network settings and reboot the base unit into captive portal mode. The unit will create its own WiFi access point so it can be reconfigured from scratch.
              </p>

              {resetSuccess ? (
                <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-md">
                  <p className="text-sm text-green-600 dark:text-green-400">
                    Reset command sent. The base unit is rebooting into captive portal mode.
                  </p>
                </div>
              ) : confirmReset ? (
                <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-md space-y-3">
                  <p className="text-sm text-red-700 dark:text-red-300 font-medium">
                    Are you sure? This will erase all network settings on {baseUnit?.base_unit_name} and the unit will go offline until reconfigured.
                  </p>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={handleReset}
                      disabled={isResetting}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50 flex items-center"
                    >
                      {isResetting ? (
                        <>
                          <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white mr-1.5" />
                          Resetting...
                        </>
                      ) : (
                        'Yes, Reset Now'
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmReset(false)}
                      disabled={isResetting}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmReset(true)}
                  disabled={isLoading || isResetting}
                  className="flex items-center px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400
                    border border-red-300 dark:border-red-700 rounded-md
                    hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                >
                  <RotateCcw className="h-4 w-4 mr-1.5" />
                  Reset to Captive Portal
                </button>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || (activeTab === 'wifi' && (!wifi.ssid || !wifi.password))}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md
                disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Applying...
                </>
              ) : (
                'Apply Configuration'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
